import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import MonitoringExpandModal, {
	MonitoringChartDataTable,
	MonitoringExpandButton,
} from "./MonitoringExpandModal";
import { adaptHistory } from "./monitoringAdapters";
import { getMonitoringHistory } from "../../../services/monitoringApi";

function citeLabel(cell) {
	if (cell === "cited") return "Cited";
	if (cell === "lost") return "Citation lost this run";
	return "Not cited";
}

function TimelinePromptCell({ text, retired }) {
	const [copied, setCopied] = useState(false);

	async function handleCopy() {
		if (!text) return;
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			toast.success("Prompt copied");
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error("Could not copy prompt");
		}
	}

	return (
		<div
			className={`monitoring__timeline-prompt-wrap${
				retired ? " monitoring__timeline-prompt-wrap--retired" : ""
			}`}
			role="cell"
		>
			<span
				className={`monitoring__timeline-prompt${
					retired ? " monitoring__timeline-prompt--retired" : ""
				}`}
				title={text}
			>
				{text}
			</span>
			<button
				type="button"
				className="monitoring__timeline-copy-btn"
				onClick={handleCopy}
				aria-label="Copy full prompt"
				title="Copy full prompt"
			>
				<i
					className={copied ? "fas fa-check" : "far fa-copy"}
					aria-hidden="true"
				/>
			</button>
		</div>
	);
}

function ChangeJournal({ events }) {
	if (!events?.length) {
		return (
			<article className="monitoring__panel monitoring__panel--journal">
				<h3 className="monitoring__panel-title">Change journal</h3>
				<p className="monitoring__empty text-muted mb-0">
					No changes recorded yet. Every run/re-audit diff lands here.
				</p>
			</article>
		);
	}

	return (
		<article className="monitoring__panel monitoring__panel--journal">
			<h3 className="monitoring__panel-title">Change journal</h3>
			<ul className="monitoring__journal list-unstyled mb-0">
				{events.map((event) => (
					<li key={event.id} className="monitoring__journal-item">
						<div className="monitoring__journal-track" aria-hidden="true">
							<span
								className={`monitoring__journal-dot monitoring__journal-dot--${event.type}${
									event.severity ? ` monitoring__journal-dot--sev-${event.severity}` : ""
								}`}
							/>
						</div>
						<div className="monitoring__journal-body">
							<p className="monitoring__journal-meta mb-0">
								{event.date} · {event.category}
							</p>
							<p className="monitoring__journal-title mb-0">{event.headline}</p>
							{/* {(event.detail || event.reportLink) && (
								<p className="monitoring__journal-detail mb-0">
									{event.detail}
									{event.reportLink && (
										<>
											{event.detail ? " · " : ""}
											<button
												type="button"
												className="monitoring__text-link"
												onClick={() =>
													toast.info(
														"Open the full report from Audit History when available."
													)
												}
											>
												view report
											</button>
										</>
									)}
								</p>
							)} */}
						</div>
					</li>
				))}
			</ul>
		</article>
	);
}

function CitationTimeline({ timeline }) {
	const dates = timeline?.dates || [];
	const prompts = (timeline?.prompts || []).filter(
		(row) => (row.status || "active") === "active"
	);
	const colCount = dates.length;

	if (!colCount) {
		return (
			<article className="monitoring__panel monitoring__panel--timeline">
				<h3 className="monitoring__panel-title monitoring__timeline-title">
					Per-prompt citation timeline — same prompt, every run{" "}
					<span className="monitoring__panel-title-muted">(prompt continuity)</span>
				</h3>
				<p className="monitoring__empty text-muted mb-0">
					No completed prompt runs yet — use “Run now” or wait for the next scheduled run.
				</p>
			</article>
		);
	}

	if (!prompts.length) {
		return (
			<article className="monitoring__panel monitoring__panel--timeline">
				<h3 className="monitoring__panel-title monitoring__timeline-title">
					Per-prompt citation timeline — same prompt, every run{" "}
					<span className="monitoring__panel-title-muted">(prompt continuity)</span>
				</h3>
				<p className="monitoring__empty text-muted mb-0">
					No actively tracked prompts to show — retired and pending candidates are
					hidden from this timeline.
				</p>
			</article>
		);
	}

	return (
		<article className="monitoring__panel monitoring__panel--timeline">
			<h3 className="monitoring__panel-title monitoring__timeline-title">
				Per-prompt citation timeline — same prompt, every run{" "}
				<span className="monitoring__panel-title-muted">(prompt continuity)</span>
			</h3>
			<div className="monitoring__timeline-scroll">
				<div
					className="monitoring__timeline-grid"
					style={{
						"--timeline-cols": colCount,
					}}
					role="table"
					aria-label="Per-prompt citation timeline"
				>
					<div className="monitoring__timeline-row monitoring__timeline-row--head" role="row">
						<div className="monitoring__timeline-prompt" role="columnheader">
							<span className="monitoring__sr-only">Prompt</span>
						</div>
						{dates.map((date, index) => (
							<div
								key={`${date}-${index}`}
								className="monitoring__timeline-date"
								role="columnheader"
							>
								{date}
							</div>
						))}
						<div className="monitoring__timeline-summary" role="columnheader">
							<span className="monitoring__sr-only">Summary</span>
						</div>
					</div>
					{prompts.map((row) => (
						<div
							key={row.id}
							className={`monitoring__timeline-row${
								row.retired ? " monitoring__timeline-row--retired" : ""
							}`}
							role="row"
						>
							<TimelinePromptCell text={row.label} retired={row.retired} />
							{row.cells.map((cell, index) => (
								<div key={`${row.id}-${index}`} className="monitoring__timeline-cell" role="cell">
									<span
										className={`monitoring__cite-dot monitoring__cite-dot--${cell}`}
										aria-label={`${row.label}: ${citeLabel(cell)}`}
									/>
								</div>
							))}
							<div
								className={`monitoring__timeline-summary monitoring__timeline-summary--${
									row.summaryKind || "never"
								}`}
								role="cell"
							>
								{row.summary}
							</div>
						</div>
					))}
				</div>
			</div>
			<ul className="monitoring__cite-legend list-unstyled mb-0" aria-label="Citation status legend">
				<li>
					<span className="monitoring__cite-dot monitoring__cite-dot--cited" aria-hidden="true" /> Cited
				</li>
				<li>
					<span className="monitoring__cite-dot monitoring__cite-dot--not" aria-hidden="true" /> Not cited
				</li>
				<li>
					<span className="monitoring__cite-dot monitoring__cite-dot--lost" aria-hidden="true" /> Citation
					lost this run
				</li>
			</ul>
		</article>
	);
}

const SCORE_TITLE = "AEO / GEO scores across re-audits";

const SCORE_TABLE_COLUMNS = [
	{ key: "date", label: "Date" },
	{ key: "aeo", label: "AEO", numeric: true },
	{ key: "geo", label: "GEO", numeric: true },
];

function ScoreLineChart({ data, height, yMin, yMax }) {
	return (
		<ResponsiveContainer width="100%" height={height}>
			<LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
				<CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
				<XAxis
					dataKey="date"
					tick={{ fontSize: 12, fill: "#6B7280" }}
					axisLine={{ stroke: "#E5E7EB" }}
					tickLine={false}
				/>
				<YAxis
					domain={[yMin, yMax]}
					tick={{ fontSize: 12, fill: "#6B7280" }}
					axisLine={false}
					tickLine={false}
					width={32}
				/>
				<Tooltip
					contentStyle={{
						borderRadius: 8,
						border: "1px solid #E5E7EB",
						fontSize: 12,
					}}
				/>
				<Legend
					wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
					iconType="circle"
					iconSize={8}
				/>
				<Line
					type="monotone"
					dataKey="aeo"
					name="AEO"
					stroke="#4F46E5"
					strokeWidth={2}
					dot={{ r: 3, fill: "#4F46E5" }}
					activeDot={{ r: 5 }}
				/>
				<Line
					type="monotone"
					dataKey="geo"
					name="GEO"
					stroke="#EA580C"
					strokeWidth={2}
					dot={{ r: 3, fill: "#EA580C" }}
					activeDot={{ r: 5 }}
				/>
			</LineChart>
		</ResponsiveContainer>
	);
}

export default function MonitoringHistory({ websiteId }) {
	const [expanded, setExpanded] = useState(false);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		async function load() {
			if (!websiteId) {
				setLoading(false);
				setError("Missing website");
				return;
			}
			setLoading(true);
			setError(null);
			const res = await getMonitoringHistory(websiteId);
			if (cancelled) return;
			if (!res.ok) {
				setError(res.message || "Failed to load history");
				setData(null);
				setLoading(false);
				return;
			}
			setData(adaptHistory(res.history));
			setLoading(false);
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [websiteId]);

	const yDomain = useMemo(() => {
		const vals = (data?.scoreSeries || [])
			.flatMap((r) => [r.aeo, r.geo])
			.filter((v) => v != null && !Number.isNaN(Number(v)))
			.map(Number);
		if (!vals.length) return { min: 0, max: 100 };
		return {
			min: Math.max(0, Math.floor(Math.min(...vals) - 5)),
			max: Math.min(100, Math.ceil(Math.max(...vals) + 5)),
		};
	}, [data]);

	if (loading) {
		return <p className="monitoring__empty text-muted mb-0">Loading history…</p>;
	}
	if (error) {
		return <p className="monitoring__empty monitoring__empty--error mb-0">{error}</p>;
	}
	if (!data) return null;

	const hasScores = data.scoreSeries?.length > 0;

	return (
		<div className="monitoring__tab-content monitoring__history">
			<div className="monitoring__detail-grid">
				<article className="monitoring__panel monitoring__panel--chart">
					<div className="monitoring__panel-header">
						<h3 className="monitoring__panel-title">{SCORE_TITLE}</h3>
						{hasScores && <MonitoringExpandButton onClick={() => setExpanded(true)} />}
					</div>
					{hasScores ? (
						<>
							<div className="monitoring__chart-wrap monitoring__chart-wrap--sm">
								<ScoreLineChart
									data={data.scoreSeries}
									height={240}
									yMin={yDomain.min}
									yMax={yDomain.max}
								/>
							</div>
							<p className="monitoring__panel-caption mb-0">
								Each point is a full re-audit. Open Audit History for the classic report.
							</p>
						</>
					) : (
						<p className="monitoring__empty text-muted mb-0">
							No re-audits recorded yet — the first scheduled re-audit creates this chart.
						</p>
					)}
				</article>

				<ChangeJournal events={data.changeEvents} />
			</div>

			<CitationTimeline timeline={data.citationTimeline} />

			<MonitoringExpandModal
				open={expanded}
				onClose={() => setExpanded(false)}
				title={SCORE_TITLE}
				chart={
					<div className="monitoring__chart-wrap monitoring__chart-wrap--expand">
						<ScoreLineChart
							data={data.scoreSeries}
							height={360}
							yMin={yDomain.min}
							yMax={yDomain.max}
						/>
					</div>
				}
				table={
					<MonitoringChartDataTable
						caption="Score history"
						columns={SCORE_TABLE_COLUMNS}
						rows={data.scoreSeries}
					/>
				}
			/>
		</div>
	);
}
